import { Injectable } from '@odinjs/odin';
import { ModalProps, ModalService } from '@philips-internal/oswald-ui-components';
import { SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { generateUUID, isMobile } from '@philips-internal/oswald-ui-utils';
import { ScheduleWaitingListData } from '../../../app/types';
import { FragmentNames } from '../../../codegen';
import DateFormatterUtil from '../../../utils/date-utils/date-formatter-util';
import PseudonymAppointmentModel from '../pseudonym-appointments-model';
import { PseudonymWaitingListData } from '../pseudonym-interfaces';
import { ScheduleItem } from '../../../interfaces/schedules-suggestions';

interface PseudonymWaitingListDataReduced {
  itemRemoved?: ScheduleWaitingListData;
  newData: Array<ScheduleWaitingListData>;
}

@Injectable
export default class PseudonymSchedulingWaitingListService {

  showPseudonymWaitingListModal(integratedScheduleItemSequence?: string): void {
    const pseudonymWaitingListData = this.mountPseudonymWaitingListData(integratedScheduleItemSequence);
    PseudonymAppointmentModel.waitingList.pseudonymWaitingListData = pseudonymWaitingListData

    const modalProps: ModalProps = {
      closeable: true,
      closeByDocument: false,
      closeByEscape: false,
      focusOnOpen: true,
      fragment: FragmentNames.PSEUDONYM_SCHEDULING_WAITING_LIST,
      noPadding: true,
      onClose: () => { return; },
      onSubmit: event => this.handleSubmitPseudonymWaitingListModal(event),
      title: MessageService.translate('patient.label.modal-scheduling-waiting-list'),
      uuid: generateUUID(),
    }

    isMobile() ? ModalService.openFullscreen(modalProps) : ModalService.openDefault(modalProps);
  }

  private mountPseudonymWaitingListData(integratedScheduleItemSequence?: string): PseudonymWaitingListData {
    const selectedPeriod = PseudonymAppointmentModel.stepSix.selectedDateRange;
    const availablePeriod = (selectedPeriod?.start && selectedPeriod?.end) ?
      [DateFormatterUtil.dateISO(selectedPeriod.start), DateFormatterUtil.dateISO(selectedPeriod.end)] :
      [] as Array<string>;

    let filteredProcedures = [];
    if (PseudonymAppointmentModel.stepTwo.procedures.length > 1) {
      filteredProcedures = PseudonymAppointmentModel.stepTwo.procedures.filter(procedure => {
        return PseudonymAppointmentModel.stepSeven.suggestions.suggestionGroups.some(group =>
          group.scheduleItems.some(scheduleItem =>
            (String(procedure.physicianSequence) === String(scheduleItem.cdMedico)) && integratedScheduleItemSequence === scheduleItem.nrSeqAgeintItem
          )
        );
      });
    } else {
      filteredProcedures = PseudonymAppointmentModel.stepTwo.procedures;
    }

    const waitingListData = {
      availablePeriod: availablePeriod,
      dependencies: {
        calendarFinalDateInitialRange: selectedPeriod?.end,
        currentPatientSelected: PseudonymAppointmentModel.stepOne.currentPatientSelected,
        healthInsuranceData: PseudonymAppointmentModel.stepOne.healthInsurance
      },
      nrSeqAgeint: PseudonymAppointmentModel.integratedScheduleSequence,
      nrSeqAgeintItem: integratedScheduleItemSequence,
      procedureList: filteredProcedures,
    } as PseudonymWaitingListData;

    return waitingListData;
  }

  private handleSubmitPseudonymWaitingListModal(event: SubmitEvent): void {
    const scheduleWaitingListData = event.record as ScheduleWaitingListData;
    PseudonymAppointmentModel.waitingList.scheduleWaitingListDataArray.push(scheduleWaitingListData);
  }

  removeItemPseudonymWaitingList(integratedScheduleItemSequence: string): void {
    const scheduleWaitingListDataArray = PseudonymAppointmentModel.waitingList.scheduleWaitingListDataArray;
    const scheduleWaitingListDataArrayReduce = scheduleWaitingListDataArray.reduce<PseudonymWaitingListDataReduced>((waitingList, item) => {
      if (item.nrSeqAgeintItem === integratedScheduleItemSequence) {
        waitingList.itemRemoved = item;
      } else {
        waitingList.newData = [...waitingList.newData, item];
      }
      return waitingList;
    }, { newData: [] });

    if (!scheduleWaitingListDataArrayReduce.itemRemoved) {
      return;
    }

    PseudonymAppointmentModel.waitingList.scheduleWaitingListDataArray = scheduleWaitingListDataArrayReduce.newData;
  }

  removeItemPseudonymSelected(suggestionGroupId: string, suggestionValue: ScheduleItem[]): void {
    if (!suggestionValue){
      return;
    }

    const suggestionList = PseudonymAppointmentModel.stepSeven.suggestions.suggestionList.map(item => {
      if (item.suggestionGroupId.toString() === suggestionGroupId) {
        const suggestion = suggestionValue.find(value => value.nrSeqAgeintItem === item.nrSeqAgeintItem);
        return suggestion ? suggestion : {} as ScheduleItem;
      }
      return item;
    });

    PseudonymAppointmentModel.stepSeven.suggestions.suggestionList =  suggestionList;
  }

}
